import { Component, HostListener, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Component({
  standalone: false,
  selector: 'app-trial-signup',
  templateUrl: './trial-signup.component.html',
  styleUrls: ['./trial-signup.component.scss'],
})
export class TrialSignupComponent implements OnInit {
  loginUrl = environment.apiUrl;

  form = {
    fn: '', ln: '', email_id: '', phone_number: '',
    bn: '', country: '',
    city: '', zip: '',
    lead_source: '',
    business_type: [] as string[],
    project_types: [] as string[], project_size: '',
    designs_per_month: '', num_users: ''
  };

  // Page state
  submitted = false;
  success = false;
  pendingInquiry = false;
  pendingMessage = '';

  // Email OTP state
  otpSent = false;
  emailVerified = false;
  otpSending = false;
  otpValue = '';
  otpLoading = false;
  otpError = '';
  otpEmailError = '';
  otpEmailConflict = false;   // true when a 409 is returned — show inline Sign In link
  attemptsRemaining: number | null = null;
  resendCooldown = 0;
  private resendTimer: any;

  loading = false;
  errorMsg = '';
  businessTypeOpen = false;
  projectTypeOpen = false;
  countryOpen = false;
  countrySearch = '';
  isOtherCountry = false;
  otherCountryText = '';

  get businessTypeLabel(): string {
    return this.form.business_type.length ? this.form.business_type.join(', ') : '-- Select --';
  }

  get projectTypeLabel(): string {
    return this.form.project_types.length ? this.form.project_types.join(', ') : '-- Select --';
  }

  get filteredCountries(): string[] {
    const q = this.countrySearch.toLowerCase();
    return q ? this.countryList.filter(c => c.toLowerCase().includes(q)) : this.countryList;
  }

  toggleBusinessTypeDropdown(): void {
    this.businessTypeOpen = !this.businessTypeOpen;
    this.projectTypeOpen = false;
    this.countryOpen = false;
  }

  toggleProjectTypeDropdown(): void {
    this.projectTypeOpen = !this.projectTypeOpen;
    this.businessTypeOpen = false;
    this.countryOpen = false;
  }

  toggleCountryDropdown(): void {
    this.countryOpen = !this.countryOpen;
    this.businessTypeOpen = false;
    this.projectTypeOpen = false;
    if (this.countryOpen) {
      this.countrySearch = '';
      setTimeout(() => {
        const el = document.getElementById('country-search-input');
        if (el) el.focus();
      }, 50);
    }
  }

  selectCountry(country: string): void {
    if (country === 'Other') {
      this.isOtherCountry = true;
      this.form.country = '';
      this.otherCountryText = '';
    } else {
      this.isOtherCountry = false;
      this.form.country = country;
      this.otherCountryText = '';
    }
    this.countryOpen = false;
    this.countrySearch = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.multiselect-dropdown')) {
      this.businessTypeOpen = false;
      this.projectTypeOpen = false;
      this.countryOpen = false;
    }
  }

  leadSourceOptions = [
    'Conference or trade show',
    'Email/Newsletter',
    'Referred by a friend',
    'Search Engine (Google, Bing, etc.)',
    'Social Media',
    'Other'
  ];

  businessTypeOptions = [
    'Residential',
    'Commercial',
    'Utility',
    'Consulting and Engineering',
    'C&I Developer',
    'Equipment Manufacturer',
    'EPC',
    'Other'
  ];

  projectTypeOptions = [
    'Rooftop',
    'Carport',
    'Ground Mount',
    'Other'
  ];

  projectSizeOptions = [
    '<20kW',
    '20 kW - 3 MW',
    '3 MW - 10 MW',
    '10+ MW',
    'All Sizes'
  ];

  designsPerMonthOptions = [
    '<20',
    '20-50',
    '50+'
  ];

  // Loaded dynamically from GET /public/signup/countries.
  // "Other" is appended at the end to route unsupported regions to CS.
  countryList: string[] = [];
  countriesLoading = true;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.http.get<string[]>(`${environment.apiUrl}nact-backend/public/signup/countries`)
      .subscribe({
        next: (countries) => {
          this.countryList = [...countries, 'Other'];
          this.countriesLoading = false;
        },
        error: () => {
          this.countriesLoading = false;
        }
      });
  }

  onBusinessTypeChange(event: Event, value: string): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.form.business_type = [...this.form.business_type, value];
    } else {
      this.form.business_type = this.form.business_type.filter(v => v !== value);
    }
  }

  onProjectTypeChange(event: Event, value: string): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.form.project_types = [...this.form.project_types, value];
    } else {
      this.form.project_types = this.form.project_types.filter(v => v !== value);
    }
  }

  sendOtp(): void {
    const email = this.form.email_id.trim().toLowerCase();
    if (!email) {
      this.otpEmailError = 'Please enter your email address.';
      return;
    }
    this.otpEmailError = '';
    this.otpEmailConflict = false;
    this.otpSending = true;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this.http.post(`${environment.apiUrl}nact-backend/public/signup/send-otp`, { email_id: email }, { headers })
      .subscribe({
        next: () => {
          this.form.email_id = email;
          this.otpSending = false;
          this.otpSent = true;
          this.otpValue = '';
          this.otpError = '';
          this.attemptsRemaining = null;
          this.startResendCooldown();
        },
        error: (err) => {
          this.otpEmailConflict = err?.status === 409;
          this.otpEmailError = err?.error?.message || 'Failed to send verification code. Please try again.';
          this.otpSending = false;
        }
      });
  }

  onOtpInput(): void {
    this.otpError = '';
    if (this.otpValue.length === 6) {
      this.verifyOtp();
    }
  }

  verifyOtp(): void {
    if (!this.otpValue.trim()) {
      this.otpError = 'Please enter the verification code.';
      return;
    }
    this.otpLoading = true;
    this.otpError = '';
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this.http.post(`${environment.apiUrl}nact-backend/public/signup/verify-otp`,
      { email_id: this.form.email_id, otp: this.otpValue.trim() }, { headers })
      .subscribe({
        next: () => {
          this.otpLoading = false;
          this.emailVerified = true;
          this.otpSent = false;
          this.attemptsRemaining = null;
        },
        error: (err) => {
          this.otpError = err?.error?.message || 'Verification failed. Please check the code and try again.';
          this.attemptsRemaining = err?.error?.attemptsRemaining ?? null;
          // OTP invalidated by server — force resend
          if (this.attemptsRemaining === 0 || this.attemptsRemaining === null && err?.status === 400) {
            if (err?.error?.message?.includes('Too many')) {
              this.otpSent = false;
              this.otpValue = '';
            }
          }
          this.otpLoading = false;
        }
      });
  }

  changeEmail(): void {
    this.emailVerified = false;
    this.otpSent = false;
    this.otpValue = '';
    this.otpError = '';
    this.otpEmailError = '';
    this.otpEmailConflict = false;
    clearInterval(this.resendTimer);
    this.resendCooldown = 0;
  }

  resendOtp(): void {
    if (this.resendCooldown > 0) return;
    this.otpError = '';
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this.http.post(`${environment.apiUrl}nact-backend/public/signup/send-otp`,
      { email_id: this.form.email_id }, { headers })
      .subscribe({
        next: () => { this.otpValue = ''; this.startResendCooldown(); },
        error: (err) => {
          this.otpError = err?.error?.message || 'Failed to resend code. Please try again.';
        }
      });
  }

  submit(): void {
    if (!this.emailVerified) {
      this.otpEmailError = 'Please verify your email before submitting.';
      return;
    }
    if (this.form.business_type.length === 0) {
      this.errorMsg = 'Please select at least one business type.';
      return;
    }
    if (this.form.project_types.length === 0) {
      this.errorMsg = 'Please select at least one type of project.';
      return;
    }
    if (this.isOtherCountry && !this.otherCountryText.trim()) {
      this.errorMsg = 'Please enter your country.';
      return;
    }

    const country = this.isOtherCountry ? this.otherCountryText.trim() : this.form.country;
    this.loading = true;
    this.errorMsg = '';
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const payload = {
      ...this.form,
      country,
      app_url: environment.baseUrl.replace(/\/$/, ''),
      ...(this.isOtherCountry ? { unsupported_region: true } : {})
    };

    this.http.post(`${environment.apiUrl}nact-backend/public/signup`, payload, { headers })
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res?.pending) {
            this.pendingInquiry = true;
            this.pendingMessage = res.message || 'Our customer success team will review your request and get back to you shortly.';
          } else {
            this.success = true;
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        error: (err) => {
          this.errorMsg = err?.error?.message || 'Signup failed. Please try again.';
          this.loading = false;
        }
      });
  }

  private startResendCooldown(): void {
    this.resendCooldown = 30;
    clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) clearInterval(this.resendTimer);
    }, 1000);
  }
}
